import { type FormEvent, useState } from 'react';
import { ApiError, confirmMfaEnrollment, startMfaEnrollment, type MfaState } from './api';

type Props = {
  csrfToken: string;
  onCompleted: (mfa: MfaState) => void;
};

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    return error.requestId === undefined
      ? error.message
      : `${error.message}（照会ID: ${error.requestId}）`;
  }
  return '多要素認証を設定できませんでした。';
}

export function MfaEnrollmentPanel({ csrfToken, onCompleted }: Props) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrollment, setEnrollment] = useState<{
    accountName: string;
    manualKey: string;
    otpauthUri: string;
  } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [completedMfa, setCompletedMfa] = useState<MfaState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function start(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setEnrollment(await startMfaEnrollment(currentPassword, csrfToken));
      setCurrentPassword('');
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await confirmMfaEnrollment(code, csrfToken);
      setRecoveryCodes(result.recoveryCodes);
      setCompletedMfa(result.mfa);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="mfa-title" className="card mfa-card">
      <p className="eyebrow">ADMIN SECURITY</p>
      <h2 id="mfa-title">管理者の多要素認証を設定</h2>
      <p>
        管理者操作を保護するため、Google Authenticator、Microsoft
        Authenticatorなどの認証アプリを登録します。
      </p>
      {error !== null && (
        <div aria-live="assertive" className="notice error" role="alert">
          {error}
        </div>
      )}

      {recoveryCodes !== null ? (
        <div className="mfa-recovery">
          <div className="notice warning">
            この復旧コードは{enrollment?.accountName ?? '現在の環境'}専用です。
            再表示できないため、安全な場所へ保存してください。各コードは1回だけ使えます。
          </div>
          <ul className="recovery-code-list">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>
                <code>{recoveryCode}</code>
              </li>
            ))}
          </ul>
          <button
            onClick={() => {
              if (completedMfa !== null) onCompleted(completedMfa);
            }}
            type="button"
          >
            保存しました。管理画面へ進む
          </button>
        </div>
      ) : enrollment === null ? (
        <form className="form-grid" onSubmit={(event) => void start(event)}>
          <label className="span-2">
            現在のパスワード
            <input
              autoComplete="current-password"
              maxLength={128}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          <button disabled={busy} type="submit">
            {busy ? '登録準備中…' : '認証アプリの登録を開始'}
          </button>
        </form>
      ) : (
        <div className="mfa-setup">
          <ol>
            <li>認証アプリで「セットアップキーを入力」を選びます。</li>
            <li>アカウント名を「{enrollment.accountName}」、キーを次の値で新規登録します。</li>
          </ol>
          <div className="notice warning">
            別環境のコードや、登録開始をやり直す前のコードは使用できません。必ず今回表示された最新キーから作成した6桁コードを入力してください。
          </div>
          <div className="manual-key" aria-label="認証アプリ登録キー">
            {enrollment.manualKey}
          </div>
          <p className="muted">
            対応端末では
            <a href={enrollment.otpauthUri}>認証アプリで開く</a>
            こともできます。
          </p>
          <form className="form-grid" onSubmit={(event) => void confirm(event)}>
            <label>
              認証アプリの6桁コード
              <input
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                minLength={6}
                onChange={(event) => setCode(event.target.value.replace(/\D/gu, ''))}
                pattern="[0-9]{6}"
                required
                value={code}
              />
            </label>
            <button disabled={busy || code.length !== 6} type="submit">
              {busy ? '確認中…' : '多要素認証を有効化'}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
